import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../Layout';
import { useAuth } from '../../auth/AuthContext';

const API = 'https://api.kallabakari.is';
const formatISK = n => new Intl.NumberFormat('is-IS', { style: 'currency', currency: 'ISK' }).format(n);

function formatProductLine(p) {
  switch (p.type) {
    case 'cake': return `Kaka: ${p.details.cake}${p.details.size ? ` (${p.details.size})` : ''}${p.details.filling ? `, Fylling: ${p.details.filling}` : ''}${p.details.text ? `, Texti: ${p.details.text}` : ''}`;
    case 'bread': return `Brauð: ${p.details.bread} × ${p.details.quantity}`;
    case 'minidonut': return `Minidonuts × ${p.details.quantity}`;
    case 'bite':     return `Smáréttur: ${p.details.name} × ${p.details.quantity}`;
    case 'bakkelsi': return `${p.details.name} × ${p.details.quantity}`;
    case 'salat':    return `${p.details.name} × ${p.details.quantity}`;
    case 'supa':     return `Súpa dagsins × ${p.details.quantity}`;
    default: return `${p.details.other || p.details.name || 'Vara'} × ${p.details.quantity ?? 1}`;
  }
}

const STATUS_LABELS = { pending: 'Í bið', confirmed: 'Staðfest', completed: 'Lokið', cancelled: 'Afbókað' };
const STATUS_CLS = {
  pending:   'bg-yellow-50 text-yellow-700 border-yellow-200',
  confirmed: 'bg-blue-50 text-blue-700 border-blue-200',
  completed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  cancelled: 'bg-red-50 text-red-600 border-red-200',
};

export default function OrderDetail() {
  const { id } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/api/company/orders/${id}`, { headers: { Authorization: token } })
      .then(res => setOrder(res.data))
      .catch(() => navigate('/'))
      .finally(() => setLoading(false));
  }, [id, token, navigate]);

  const handleDelete = async () => {
    if (!window.confirm('Eyða þessari pöntun?')) return;
    await axios.delete(`${API}/api/company/orders/${id}`, { headers: { Authorization: token } });
    navigate('/');
  };

  if (loading) {
    return (
      <Layout>
        <p className="text-sm text-gray-400">Hleður pöntun...</p>
      </Layout>
    );
  }

  if (!order) return null;

  const dateObj = new Date(order.date);
  const dateStr = dateObj.toLocaleDateString('is-IS', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });

  return (
    <Layout>
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate(-1)} className="text-sm text-gray-400 hover:text-gray-600 transition-colors">← Til baka</button>
        <h1 className="text-xl font-semibold text-gray-900">Pöntunarupplýsingar</h1>
      </div>

      <div className="max-w-lg space-y-4">
        {/* Info */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4">Upplýsingar</h2>
          <dl className="space-y-2.5">
            <div className="flex">
              <dt className="w-32 text-sm text-gray-400">Dagsetning</dt>
              <dd className="text-sm font-medium text-gray-800 capitalize">{dateStr}</dd>
            </div>
            <div className="flex">
              <dt className="w-32 text-sm text-gray-400">Afhending</dt>
              <dd className="text-sm font-medium text-gray-800">
                {order.deliveryType === 'delivery' ? 'Sent' : 'Sótt'}
              </dd>
            </div>
            {order.pickupTime && (
              <div className="flex">
                <dt className="w-32 text-sm text-gray-400">
                  {order.deliveryType === 'delivery' ? 'Afhendingartími' : 'Afhendingartími'}
                </dt>
                <dd className="text-sm font-semibold text-stone-600">{order.pickupTime}</dd>
              </div>
            )}
            <div className="flex">
              <dt className="w-32 text-sm text-gray-400">Staða</dt>
              <dd>
                <span className={`inline-flex text-xs font-medium px-2 py-0.5 rounded-full border ${STATUS_CLS[order.status] || ''}`}>
                  {STATUS_LABELS[order.status] || order.status}
                </span>
              </dd>
            </div>
            <div className="flex">
              <dt className="w-32 text-sm text-gray-400">Upphæð</dt>
              <dd className="text-sm font-semibold text-gray-800">{formatISK(order.totalAmount)}</dd>
            </div>
            {order.note && (
              <div className="flex">
                <dt className="w-32 text-sm text-gray-400">Athugasemd</dt>
                <dd className="text-sm text-gray-600 italic">"{order.note}"</dd>
              </div>
            )}
          </dl>
        </div>

        {/* Image */}
        {order.image && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4">Mynd</h2>
            <img
              src={`https://api.kallabakari.is/${order.image}`}
              alt="Pöntunarmynd"
              className="w-48 h-48 object-cover rounded-lg border border-stone-100"
            />
          </div>
        )}

        {/* Products */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4">Vörur</h2>
          <ul className="space-y-2">
            {order.products.map((p, i) => (
              <li key={i} className="text-sm text-gray-700 bg-stone-50 border border-stone-100 rounded-lg px-3 py-2">
                {formatProductLine(p)}
              </li>
            ))}
          </ul>
        </div>

        {/* Actions */}
        <button
          onClick={handleDelete}
          className="bg-red-50 hover:bg-red-100 text-red-600 border border-red-100 text-sm font-medium px-4 py-2 rounded-lg transition-colors"
        >
          Eyða pöntun
        </button>
      </div>
    </Layout>
  );
}
